import React from 'react';
import { Slot, DropEvent } from './Slot';
import { ZoneLayout } from '@roc-digital/ui-web';
import { ArticleViewProps, mapToArticleContent, useMethod} from '@roc-digital/ui-lib';
import { ArticleSchema } from '@roc-digital/mxm-base/src/data';
import { Zone } from './types';
import { listArticlesByScopesV2, removeArticleTags } from '@roc-digital/mxm-base/logic';
import { ArticleViewContainer } from './ArticleViewContainer';
import { useContextMenu } from './useContextMenu';
import { openDeZoneArticleModal } from './DeZoneArticleModal';
import { fillArticles, getMaxArticlesForLayout } from './utils';

export interface DnDZonePreviewProps {
  zoneId: string;
  layoutId: number;
  onDrop: (event: DropEvent) => void;

  zone: Zone;
  onPin: (article: ArticleSchema) => void;
}

const ZoneContext = React.createContext<any>({});

export function DnDZonePreview(props: DnDZonePreviewProps) {

  // console.log(props.articles, props.zone);
  const articlesQuery = useMethod(listArticlesByScopesV2);
  const ctxMenuDataRef = React.useRef('');
  const articlesRef = React.useRef<ArticleSchema[]>([]);

  const scopes = props.zone?.tags?.map(z => z.scope) as string[] || [];
  const scopesKey= scopes.join(',');

  React.useEffect(() => {
    if(scopes.length) {
      articlesQuery.run(scopes, 1, 12);
    } else {
      articlesQuery.setResult([]);
    }
  }, [scopesKey]);

  const [articles, articleContents] = React.useMemo(() => {
    const tagArticles = (articlesQuery.result || [])
    .filter(a => props.zone.articles.findIndex(ab => ab.id === a.id) === -1) // Filter out tag articles that are in the props.


    const articles = fillArticles(props.zone.articles, getMaxArticlesForLayout(props.zone.web_layout))
    .map(a => {
      if(a?.id && a.id.startsWith('temp_') === true) {
        const tagA = tagArticles.shift();
        if(tagA) return tagA
      }

      return a;
    });
    
    const articleContents = articles.map(a => mapToArticleContent(a, {}, true));

    return [articles, articleContents];
  }, [articlesQuery.result, props.zone.articles]);
  articlesRef.current = articles || []


    const dropdown = useContextMenu({
      onAction: (action) => {
        if(!ctxMenuDataRef.current) return;
        const articleId = ctxMenuDataRef.current;
        ctxMenuDataRef.current = '';
        const article = articlesRef.current.find(a => a.id === articleId);
        if(!article) return;
        if(action === 'pin') {
          props.onPin(article);
        } else if(action === 'remove') {
          if(!props.zone.tags) return;
          openDeZoneArticleModal({
            article,
            zone: props.zone,
            onYes: async () => {
              props.onPin(article);
              removeArticleFromZone(article, props.zone).finally(articlesQuery.reload)
            }
          })
        }
      }
    })

  return <ZoneContext.Provider key={props.zoneId} value={{
    zoneId: props.zoneId,
    onDrop: props.onDrop,
    staticArticlesIds: props.zone.articleIds || [],
    onMenu: (event, articleId) => {
      ctxMenuDataRef.current = articleId
      dropdown.open(event, props.zone, articleId);
    }
  }}>
    <ZoneLayout
      articles={articleContents}
      layoutId={props.layoutId as any}
      onAction={() => {}}
      ArticleViewComponent={ZonePreview}
    />
    {dropdown.jsx}
  </ZoneContext.Provider>;
}

function ZonePreview(props: ArticleViewProps) {
  const state = React.useContext(ZoneContext);
  let article = props.article;

  if(article && !article.headline) {
    article = {
      ...article,
      headline: 'Drop article here'
    }
  }

  const isStaticArticle = state.staticArticlesIds.includes(article.id);

  return <Slot
    getItem={() => ({area: state.zoneId, slot: props.index || 0, articleId: props.article?.id})}
    onDrop={state.onDrop}
    className={props.className}
    disableDrag={isStaticArticle === false}
  >
    <ArticleViewContainer onMenu={state.onMenu} {...props} article={article} isStatic={isStaticArticle} />
    {/* <ArticleView {...props} article={article}/> */}
  </Slot>
}

async function removeArticleFromZone(article: ArticleSchema, zone: Zone) {
  if(!zone.tags?.length) return;
  for(let tag of zone.tags) {
    await removeArticleTags(article.id, tag.id as any).catch(e => console.error(e));
  }
}